import Fuse from "fuse.js";
import { useEffect, useMemo, useState } from "react";
import useDebounce from "./useDebounce";
const useSearch = (data, searchValue) => {
  const [searchResult, setSearchResult] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
  }, [searchValue]);

  const initClientSideSearch = data => {
    if (!data) {
      return;
    }
    const options = {
      threshold: 0.0,
      ignoreLocation: true,
      minMatchCharLength: 3,
      includeMatches: true,
      keys: ["title", "description"],
    };

    const search = new Fuse(data, options);

    return search;
  };

  const fuseSearch = useMemo(
    () => initClientSideSearch(data),
    []
  );

  const highlightSearch = (node, searchValue) => {
    const { item } = node || {};
    const title = item?.title;
    const description = item?.description;

    let isValid = true;
    const searchedTextIndex = title
      ?.toLowerCase()
      ?.indexOf(searchValue.toLowerCase());

    const searchedValueStart = searchedTextIndex;
    const searchedValueEnd =
      searchedTextIndex + searchValue?.length - 1;
    const highlight = searchedTextIndex !== -1;

    if (description?.includes("child-pages")) {
      isValid = false;
    }

    return {
      searchedValueStart,
      searchedValueEnd,
      highlight,
      isValid,
    };
  };

  const performSearch = searchValue => {
    setIsLoading(true);
    const searchResult = fuseSearch
      ? fuseSearch.search(searchValue)
      : [];

    const possibleKeywords = searchValue
      ?.split(" ")
      ?.map(item =>
        item?.length >= 3 ? item?.trim() : undefined
      );

    for (let keyword of possibleKeywords) {
      if (keyword) {
        const keywordSearchResult = fuseSearch?.search(
          keyword
        );
        searchResult?.push(...keywordSearchResult);
      }
    }

    const uniqueNode = {};
    const filteredSearchResult = [];

    for (let node of searchResult) {
      if (!uniqueNode[node?.item?.uri]) {
        uniqueNode[node?.item?.uri] = true;
        const {
          searchedValueStart,
          searchedValueEnd,
          highlight,
          isValid,
        } = highlightSearch(node, searchValue);

        if (isValid) {
          filteredSearchResult?.push({
            ...node,
            searchedValueStart,
            searchedValueEnd,
            matches: null,
            highlight,
          });
        }
      }
    }
    setSearchResult(filteredSearchResult);
    setIsLoading(false);
  };

  const debouncedSearch = useDebounce(performSearch);

  useEffect(() => {
    if (searchValue?.length >= 3) {
      debouncedSearch(searchValue);
    }
  }, [searchValue]);

  return { searchResult, isLoading };
};

export default useSearch;
